import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Backdrop, CircularProgress, Box, Typography } from '@mui/material';

interface LoadingContextType {
  isLoading: boolean;
  loadingMessage: string;
  showLoading: (message?: string) => void;
  hideLoading: () => void;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const useLoading = (): LoadingContextType => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

interface LoadingProviderProps {
  children: ReactNode;
}

export const LoadingProvider: React.FC<LoadingProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');

  const showLoading = React.useCallback((message = 'Loading...') => {
    setLoadingMessage(message);
    setIsLoading(true);
  }, []);

  const hideLoading = React.useCallback(() => {
    setIsLoading(false);
    setLoadingMessage('Loading...');
  }, []);

  const value = React.useMemo<LoadingContextType>(() => ({
    isLoading,
    loadingMessage,
    showLoading,
    hideLoading,
  }), [isLoading, loadingMessage, showLoading, hideLoading]);

  return (
    <LoadingContext.Provider value={value}>
      {children}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
        }}
        open={isLoading}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={2}
        >
          <CircularProgress 
            sx={{color:"hsl(19 101% 51% / 1)"}} 
            size={60}
            thickness={4}
          />
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              color: 'white',
              textAlign: 'center',
              fontWeight: 500,
            }}
          >
            {loadingMessage}
          </Typography>
        </Box>
      </Backdrop>
    </LoadingContext.Provider>
  );
};